/**
 * Benefits tab on the employee profile.
 *
 * Lists every active benefit_election for the employee + lets admins
 * manually enroll the employee in a plan (no formal enrollment
 * window required). Used as the data source the COBRA flow reads
 * from at offboard time — without rows here, COBRA has nothing to
 * continue.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  HeartPulse,
  Loader2,
  Plus,
  ShieldCheck,
  Trash2,
} from "lucide-react";

import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/hooks/useToast";
import {
  createEmployeeElection,
  listEmployeeElections,
  listPlans,
  terminateEmployeeElection,
  type AdminEnrolledElection,
  type BenefitPlan,
  type Tier,
} from "@/api/benefits";

const TIER_LABELS: Record<Exclude<Tier, "waived">, string> = {
  employee_only: "Employee Only",
  employee_spouse: "Employee + Spouse",
  employee_children: "Employee + Children",
  family: "Family",
};

const PLAN_TYPE_LABELS: Record<string, string> = {
  medical: "Medical",
  dental: "Dental",
  vision: "Vision",
  life: "Life",
  disability_short: "Disability — Short Term",
  disability_long: "Disability — Long Term",
  accident: "Accident",
  critical_illness: "Critical Illness",
  hsa: "HSA",
  fsa: "FSA",
  retirement_401k: "401(k)",
};

function fmtMoney(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

interface EmployeeBenefitsTabProps {
  employeeId: string;
  /** Used purely for the modal placeholder copy. */
  employeeName?: string;
}

export function EmployeeBenefitsTab({
  employeeId,
  employeeName,
}: EmployeeBenefitsTabProps) {
  const { addToast } = useToast();
  const [elections, setElections] = useState<
    AdminEnrolledElection[] | null
  >(null);
  const [showAdd, setShowAdd] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = async () => {
    try {
      setElections(await listEmployeeElections(employeeId));
    } catch (err) {
      console.warn("EmployeeBenefitsTab: failed to load", err);
      setElections([]);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const handleTerminate = async (election: AdminEnrolledElection) => {
    setBusyId(election.id);
    try {
      await terminateEmployeeElection(election.id);
      addToast("success", "Election terminated");
      await reload();
    } catch {
      addToast("error", "Couldn't terminate election");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <Card>
        <CardHeader>
          <CardTitle>
            <span className="inline-flex items-center gap-2">
              <HeartPulse className="h-4 w-4 text-accent" />
              Benefits enrollments
            </span>
          </CardTitle>
          <Button size="sm" onClick={() => setShowAdd(true)}>
            <Plus className="h-3.5 w-3.5" />
            Add enrollment
          </Button>
        </CardHeader>

        {!elections ? (
          <div className="py-10 text-center text-text-muted">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : elections.length === 0 ? (
          <div className="py-8 text-center text-sm">
            <ShieldCheck className="h-7 w-7 mx-auto mb-2 text-text-muted opacity-40" />
            <p className="text-text-primary font-medium">
              No active benefit enrollments
            </p>
            <p className="text-text-muted mt-1">
              Add an enrollment to record what plans this employee is
              covered by. COBRA reads from these rows at offboard time.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border/40">
            {elections.map((el) => (
              <li
                key={el.id}
                className="py-3 flex items-start justify-between gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-text-primary truncate">
                      {el.plan_name || "(plan removed)"}
                    </span>
                    {el.plan_type && (
                      <Badge variant="default">
                        {PLAN_TYPE_LABELS[el.plan_type] || el.plan_type}
                      </Badge>
                    )}
                    {el.tier && el.tier !== "waived" && (
                      <Badge variant="info">
                        {TIER_LABELS[el.tier as Exclude<Tier, "waived">] ||
                          el.tier}
                      </Badge>
                    )}
                    {el.status === "cobra_continuation" && (
                      <Badge variant="warning">COBRA continuation</Badge>
                    )}
                  </div>
                  <p className="text-xs text-text-muted mt-0.5">
                    {el.carrier_name && <>{el.carrier_name} · </>}
                    Employee {fmtMoney(el.employee_cost)}/mo · Employer{" "}
                    {fmtMoney(el.employer_cost)}/mo
                    {el.effective_date && (
                      <> · Effective {fmtDate(el.effective_date)}</>
                    )}
                    {el.termination_date && (
                      <> · Ended {fmtDate(el.termination_date)}</>
                    )}
                  </p>
                </div>
                {el.status !== "terminated" &&
                  el.status !== "cobra_continuation" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleTerminate(el)}
                      disabled={busyId === el.id}
                      title="Terminate this enrollment"
                    >
                      <Trash2 className="h-4 w-4 text-danger" />
                    </Button>
                  )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <AddEnrollmentModal
        isOpen={showAdd}
        onClose={() => setShowAdd(false)}
        employeeId={employeeId}
        employeeName={employeeName}
        onAdded={async () => {
          setShowAdd(false);
          await reload();
        }}
      />
    </motion.div>
  );
}

// ── Add-enrollment modal ──────────────────────────────────────────


function AddEnrollmentModal({
  isOpen,
  onClose,
  employeeId,
  employeeName,
  onAdded,
}: {
  isOpen: boolean;
  onClose: () => void;
  employeeId: string;
  employeeName?: string;
  onAdded: () => void | Promise<void>;
}) {
  const { addToast } = useToast();
  const [plans, setPlans] = useState<BenefitPlan[] | null>(null);
  const [planId, setPlanId] = useState("");
  const [tier, setTier] = useState<Exclude<Tier, "waived">>("employee_only");
  const [effectiveDate, setEffectiveDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    listPlans()
      .then((rows) => {
        const active = rows.filter((p) => p.is_active);
        setPlans(active);
        if (active[0]) setPlanId(active[0].id);
      })
      .catch(() => setPlans([]));
  }, [isOpen]);

  const submit = async () => {
    if (!planId) {
      addToast("error", "Pick a plan first");
      return;
    }
    setBusy(true);
    try {
      await createEmployeeElection(employeeId, {
        plan_id: planId,
        tier,
        effective_date: effectiveDate,
      });
      addToast("success", "Enrollment added");
      await onAdded();
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { detail?: unknown } };
      };
      const detail =
        typeof ax?.response?.data?.detail === "string"
          ? ax.response.data.detail
          : "Couldn't add enrollment";
      addToast("error", detail);
    } finally {
      setBusy(false);
    }
  };

  const selectedPlan = plans?.find((p) => p.id === planId);
  const tierRate = selectedPlan?.tier_rates.find((r) => r.tier === tier);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        employeeName ? `Add enrollment — ${employeeName}` : "Add enrollment"
      }
      size="md"
    >
      {!plans ? (
        <div className="py-8 text-center text-text-muted">
          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
        </div>
      ) : plans.length === 0 ? (
        <div className="py-8 text-center text-sm text-text-muted">
          No active benefit plans configured. Add plans in{" "}
          <strong>Settings → Benefits → Plans</strong> first.
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-text-muted mb-1">
              Plan
            </label>
            <Select
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              options={plans.map((p) => ({
                value: p.id,
                label: `${p.name} (${
                  PLAN_TYPE_LABELS[p.plan_type] || p.plan_type
                })`,
              }))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-muted mb-1">
              Coverage tier
            </label>
            <Select
              value={tier}
              onChange={(e) =>
                setTier(e.target.value as Exclude<Tier, "waived">)
              }
              options={[
                { value: "employee_only", label: "Employee Only" },
                { value: "employee_spouse", label: "Employee + Spouse" },
                { value: "employee_children", label: "Employee + Children" },
                { value: "family", label: "Family" },
              ]}
            />
          </div>
          {tierRate && (
            <div className="rounded-md border border-accent/30 bg-accent/5 p-3 text-xs">
              <div className="text-text-primary">
                Employee pays{" "}
                <strong>{fmtMoney(tierRate.employee_cost)}/mo</strong> ·
                Employer pays{" "}
                <strong>{fmtMoney(tierRate.employer_cost)}/mo</strong>
              </div>
            </div>
          )}
          <Input
            label="Effective date"
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
          />
          <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={submit} isLoading={busy}>
              <Plus className="h-4 w-4" />
              Add enrollment
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default EmployeeBenefitsTab;
